// Preloads every scene asset before the flight is scrollable, warming the browser's
// HTTP cache so the engine's own lazy fetch(clip) later hits cache instantly. Stills are
// always preloaded; the ~66 MB of clips are gated by the same conditions under which the
// engine would actually use them (skipped for reduced-motion, Save-Data, and 2G/3G).
import { CLIP_URLS, STILL_URLS, USE_CLIPS } from './config';

interface NetworkInformation {
  saveData?: boolean;
  effectiveType?: string;
}

function shouldPreloadClips(): boolean {
  if (!USE_CLIPS) return false;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  const conn = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  if (conn) {
    if (conn.saveData) return false;
    if (conn.effectiveType && ['slow-2g', '2g', '3g'].includes(conn.effectiveType)) return false;
  }
  return true;
}

// The exact set of URLs this device should download before scrolling is enabled.
export function preloadUrls(): string[] {
  return shouldPreloadClips() ? [...STILL_URLS, ...CLIP_URLS] : [...STILL_URLS];
}

// Downloads all assets, reporting 0..100 as bytes arrive. Individual failures are
// swallowed (a missing asset must not trap the user); the caller adds a wall-clock
// failsafe on top. Resolves once every request has settled and fonts are ready.
export async function preloadAll(
  onProgress: (pct: number) => void,
  signal: AbortSignal,
): Promise<void> {
  const urls = preloadUrls();
  let total = 0;
  let loaded = 0;
  const report = () => onProgress(total > 0 ? Math.min(99, Math.round((loaded / total) * 100)) : 0);

  await Promise.allSettled(
    urls.map(async (url) => {
      try {
        const res = await fetch(url, { signal, cache: 'force-cache' });
        if (!res.ok) return;
        const declared = Number(res.headers.get('content-length')) || 0;
        if (declared) { total += declared; report(); }
        const body = res.body;
        if (body && typeof body.getReader === 'function') {
          const reader = body.getReader();
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            if (value) { loaded += value.length; if (!declared) total += value.length; report(); }
          }
        } else {
          const blob = await res.blob();
          loaded += blob.size;
          if (!declared) total += blob.size;
          report();
        }
      } catch {
        // ignore: a failed asset falls back to lazy loading / the still poster
      }
    }),
  );

  // Fonts (Google Fonts) — swaps to system fonts on failure, so never block hard.
  try { if (document.fonts?.ready) await document.fonts.ready; } catch { /* noop */ }

  onProgress(100);
}
